import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Package, Plus, Edit, Trash2, Search,
  Image as ImageIcon, Loader2, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

export default function VendorProducts() {
  const { vendor, token } = useVendor();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) setFilter(filterParam);
  }, [searchParams]);

  useEffect(() => {
    // Authentication is handled by VendorProtectedRoute
    if (vendor && token) {
      fetchProducts();
    }
  }, [vendor, token]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vendor/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    setDeleteLoading(productId);
    try {
      await axios.delete(`${API_URL}/api/vendor/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      setProducts(products.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setDeleteLoading(null);
    }
  };

  const toggleProductStatus = async (product) => {
    try {
      await axios.put(
        `${API_URL}/api/vendor/products/${product.id}`,
        { is_active: !product.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, is_active: !p.is_active } : p
      ));
      toast.success(`Product ${product.is_active ? 'hidden' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  // Filter products based on search and filter param
  const filteredProducts = products.filter(product => {
    // Apply inventory filter
    if (filter === 'outofstock' && product.inventory !== 0) return false;
    if (filter === 'lowstock' && (product.inventory === 0 || product.inventory > 5)) return false;
    
    // Apply search
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(search) ||
      product.sku?.toLowerCase().includes(search) ||
      product.description?.toLowerCase().includes(search)
    );
  });

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-products-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Products</h1>
            <p className="text-gray-500 text-sm">{products.length} total products</p>
          </div>
          <Link to="/vendor/products/new">
            <Button className="bg-green-600 hover:bg-green-700" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'outofstock', label: 'Out of Stock' },
              { value: 'lowstock', label: 'Low Stock' }
            ].map(f => (
              <Button
                key={f.value}
                variant={filter === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f.value)}
                className={filter === f.value ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="product-search"
              />
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'all' ? 'No products yet' : `No ${filter === 'outofstock' ? 'out of stock' : 'low stock'} products`}
              </h3>
              <p className="text-gray-500 mb-6">
                {filter === 'all' ? 'Start adding products to your store' : 'Great! Your inventory is healthy'}
              </p>
              {filter === 'all' && (
                <Link to="/vendor/products/new">
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Your First Product
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`product-card-${product.id}`}>
                {/* Product Image */}
                <div className="relative aspect-square bg-gray-100">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-gray-300" />
                    </div>
                  )}
                  
                  {/* Approval Status Badge */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {product.approval_status === 'pending' && (
                      <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">
                        Pending Approval
                      </Badge>
                    )}
                    {product.approval_status === 'rejected' && (
                      <Badge className="bg-red-100 text-red-800 border border-red-300">
                        Rejected
                      </Badge>
                    )}
                    {product.approval_status === 'approved' && (
                      <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {product.is_active ? 'Live' : 'Hidden'}
                      </Badge>
                    )}
                    {!product.approval_status && (
                      <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {product.is_active ? 'Active' : 'Hidden'}
                      </Badge>
                    )}
                  </div>

                  {/* Inventory Badge */}
                  {product.inventory <= 5 && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-red-100 text-red-800">
                        {product.inventory === 0 ? 'Out of Stock' : `Low: ${product.inventory}`}
                      </Badge>
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 line-clamp-1 mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-500 mb-2">SKU: {product.sku}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      {product.sale_price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-green-600">{formatPrice(product.sale_price)}</span>
                          <span className="text-sm text-gray-400 line-through">{formatPrice(product.price)}</span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">{formatPrice(product.price)}</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">Stock: {product.inventory}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link to={`/vendor/products/${product.id}/edit`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleProductStatus(product)}
                      title={product.is_active ? 'Hide Product' : 'Show Product'}
                    >
                      {product.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      disabled={deleteLoading === product.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {deleteLoading === product.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </VendorLayout>
  );
}
